import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserIcon, ChevronDownIcon, ClipboardIcon, LogoutIcon } from '@/icons';

interface UserMenuProps {
  username: string;
  onLogout: () => Promise<void>;
}

export function UserMenu({ username, onLogout }: UserMenuProps) {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close on outside click
  useEffect(() => {
    if (!showUserMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowUserMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showUserMenu]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        data-testid="user-menu-button"
        onClick={() => setShowUserMenu(!showUserMenu)}
        className="px-3 sm:px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors flex items-center gap-2 touch-manipulation"
        aria-expanded={showUserMenu}
        aria-haspopup="true"
        aria-label={`User menu for ${username}`}
      >
        <UserIcon />
        <span className="hidden sm:inline">{username}</span>
        <ChevronDownIcon
          size="sm"
          className={`transition-transform ${showUserMenu ? 'rotate-180' : ''}`}
        />
      </button>
      {showUserMenu && (
        <div
          className="absolute right-0 mt-2 w-48 bg-slate-800 border border-slate-700 rounded-lg shadow-lg py-1 z-10"
          role="menu"
        >
          <button
            data-testid="activity-logs-menu-item"
            role="menuitem"
            onClick={() => {
              setShowUserMenu(false);
              navigate('/activity');
            }}
            className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 transition-colors flex items-center gap-2"
          >
            <ClipboardIcon size="sm" />
            Activity Logs
          </button>
          <div className="border-t border-slate-700 my-1"></div>
          <button
            data-testid="logout-menu-item"
            role="menuitem"
            onClick={async () => {
              setShowUserMenu(false);
              await onLogout();
              navigate('/');
            }}
            className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-slate-700 transition-colors flex items-center gap-2"
          >
            <LogoutIcon size="sm" />
            Logout
          </button>
        </div>
      )}
    </div>
  );
}
